import { useEffect, useRef } from 'react';
import { MotionValue, useAnimationFrame, useMotionValue } from 'framer-motion';

/**
 * Drives an auto-advancing slideshow. Returns a 0→1 progress value for the current slide;
 * calls onAdvance when it completes. Progress resets whenever `index` changes.
 */
export function useAutoAdvance(index: number, duration: number, paused: boolean, onAdvance: () => void): MotionValue<number> {
  const progress = useMotionValue(0);
  const elapsed = useRef(0);
  const advance = useRef(onAdvance);
  advance.current = onAdvance;

  useEffect(() => {
    elapsed.current = 0;
    progress.set(0);
  }, [index, progress]);

  useAnimationFrame((_, delta) => {
    if (paused) return;
    elapsed.current += Math.min(delta, 100);
    const p = Math.min(1, elapsed.current / duration);
    progress.set(p);
    if (p >= 1) {
      elapsed.current = 0;
      advance.current();
    }
  });

  return progress;
}
